import React, { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IoMdArrowRoundBack } from "react-icons/io";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";

import { UserContext } from "../../Context";
import { useCourses } from "./Hooks/useCourses";
import { TRAINING } from "../../routes";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import { LMS_TOAST_MESSAGES, COURSE_TOAST_MESSAGES } from "./constants";

const MODULE_TYPES = [
  { label: "Video", value: "video" },
  { label: "PDF", value: "pdf" },
  { label: "PPT", value: "ppt" },
];

const CourseActionPage = () => {
  const navigate = useNavigate();
  const { courseEditId } = useParams();
  const isEditMode = Boolean(courseEditId);
  const [initialModules, setInitialModules] = useState([]);
  const [initialCourse, setInitialCourse] = useState(null);
  const { user } = useContext(UserContext);
  const isHR =["HR", "DIRECTOR_MANAGEMENT"].includes(user?.role);

  const {
    loading,
    fetchCourseById,
    submitCourse,
    editCourse,
    submitModule,
    editModule,
    removeModule,
  } = useCourses();
  const [course, setCourse] = useState({
    title: "",
    description: "",
    image: null,
    existingImage: null,
    order: 1,
    is_active: true,
  });

  const [modules, setModules] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputClasses =
    "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm " +
    "focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-none";

  /* ----------------------------------
     Load course data in EDIT mode
  ---------------------------------- */
  const getFileName = (file) => {
    if (!file) return "";
    if (typeof file === "string") return file.split("/").pop().split("?")[0];
    return "";
  };

  const loadCourse = async () => {
    try {
      const data = await fetchCourseById(courseEditId);
      const mappedCourse = {
        title: data.title,
        description: data.description,
        image: null,
        existingImage: data.image,
        order: data.order ?? 0,
        is_active: data.is_active ?? true,
      };
      setCourse(mappedCourse);
      setInitialCourse(JSON.parse(JSON.stringify(mappedCourse)));
      const mappedModules = data.modules.map((m, index) => ({
        id: m.id,
        title: m.title,
        content_type: m.content_type,
        file: m.file,
        order: m.order?.toString() ?? String(index + 1),
        prev_file: m.prev_file,
        duration_minutes: m.duration_seconds
          ? Math.ceil(m.duration_seconds / 60)
          : "",
      }));
      setModules(mappedModules);
      setInitialModules(JSON.parse(JSON.stringify(mappedModules)));
    } catch (err) {
      await new Promise(() => setTimeout(() => navigate(-1), 1000));
    }
  };

  const getChangedCourseFields = (initial, current) => {
    if (!initial) return current;

    const changed = {};

    Object.keys(current).forEach((key) => {
      switch (key) {
        case "existingImage":
          break;
        case "image":
          if (current.image instanceof File) {
            changed.image = current.image;
          }
          break;
        case "order":
          if (Number(current.order) !== Number(initial.order)) {
            changed.order = current.order;
          }
          break;
        default:
          if (current[key] !== initial[key]) {
            changed[key] = current[key];
          }
          break;
      }
    });

    return changed;
  };

  const getChangedFields = (initial, current) => {
    const changed = {};
    // module creation time file not selected
    if (!initial) {
      return {
        title: current.title,
        content_type: current.content_type,
        order: current.order,
        file: current.file,
        duration_seconds: Number(current.duration_minutes || 0) * 60,
      };
    }
    if (initial.title !== current.title) {
      changed.title = current.title;
    }
    if (initial.content_type !== current.content_type) {
      changed.content_type = current.content_type;
    }
    if (Number(initial.order) !== Number(current.order)) {
      changed.order = Number(current.order);
    }
    if (Number(initial.duration_minutes) !== Number(current.duration_minutes)) {
      changed.duration_seconds = Number(current.duration_minutes || 0) * 60;
    }
    // File logic:
    // initial.file → string (S3)
    // current.file → File (binary)
    if (current.file instanceof File) {
      changed.file = current.file;
    }
    return changed;
  };

  useEffect(() => {
    if (!isHR) return;

    if (!isEditMode) {
      setModules([
        {
          title: "",
          content_type: "video",
          file: null,
          order: 1,
          duration_minutes: "",
        },
      ]);
      return;
    }

    loadCourse();
  }, [courseEditId]);

  if (!isHR) return null;

  //file type validation
  const FILE_TYPE_MAP = {
    video: ["video/mp4", "video/webm", "video/ogg"],
    pdf: ["application/pdf"],
    ppt: [
      "application/vnd.ms-powerpoint", // .ppt
      "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
    ],
  };

  const isValidFileType = (contentType, file) => {
    if (!file) return false;

    const allowedTypes = FILE_TYPE_MAP[contentType];
    return allowedTypes?.includes(file.type);
  };

  /* ----------------------------------
     Module handlers
  ---------------------------------- */
  const handleAddModule = () => {
    setModules((prev) => [
      ...prev,
      {
        title: "",
        content_type: "video",
        file: null,
        order: String(prev.length + 1),
        duration_minutes: "",
      },
    ]);
  };

  const handleRemoveModule = (index) => {
    setModules((prev) =>
      prev.map((m, i) => (i === index ? { ...m, isDeleted: true } : m)),
    );
  };

  const handleModuleChange = (index, key, value) => {
    setModules((prev) =>
      prev.map((m, i) => (i === index ? { ...m, [key]: value } : m)),
    );
  };

  /* ----------------------------------
     Submit handler (Create / Edit)
  ---------------------------------- */
  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!course.title.trim()) {
      toast.error(COURSE_TOAST_MESSAGES.COURSE_TITLE_REQUIRED);
      return;
    }
    if (!course.description.trim()) {
      toast.error(COURSE_TOAST_MESSAGES.COURSE_DESCRIPTION_REQUIRED);
      return;
    }
    setIsSubmitting(true);
    /*  Course */
    try {
      let activeCourseId = courseEditId;
      const changedFields = getChangedCourseFields(initialCourse, course);
      const courseForm = new FormData();
      Object.entries(changedFields).forEach(([key, val]) => {
        if (val === undefined || val === null) return;
        if (key === "order") {
          courseForm.append(key, Number(val));
        } else if (key === "image") {
          if (val instanceof File) {
            courseForm.append(key, val);
          }
        } else {
          courseForm.append(key, val);
        }
      });
      if (isEditMode) {
        if (Object.keys(changedFields).length !== 0) {
          await editCourse(courseEditId, courseForm);
        }
      } else {
        const created = await submitCourse(courseForm);
        activeCourseId = created.id;
      }

      /* 2️⃣ Modules */
      for (const module of modules) {
        try {
          if (module.isDeleted) {
            if (module.id) await removeModule(activeCourseId, module.id);
            continue;
          }
          if (!module.title.trim()) {
            toast.error(LMS_TOAST_MESSAGES.TITLE_IS_REQUIRED);
            return;
          }
          const initial = initialModules.find((m) => m.id === module.id);
          if (!initial && !(module.file instanceof File)) {
            toast.error(LMS_TOAST_MESSAGES.FILE_IS_REQUIRED);
            return;
          }
          // Validate file type matches content_type
          if (module.file instanceof File) {
            if (!isValidFileType(module.content_type, module.file)) {
              toast.error(
                `Selected file does not match the chosen content type (${module.content_type.toUpperCase()})`,
              );
              setIsSubmitting(false);
              return;
            }
          }

          if (
            initial &&
            initial.content_type !== module.content_type &&
            !(module.file instanceof File)
          ) {
            toast.error(LMS_TOAST_MESSAGES.CONTENT_TYPE_FILE_REQUIRED);
            return;
          }
          const changedFields = getChangedFields(initial, module);
          if (module.id && Object.keys(changedFields).length === 0) {
            continue;
          }
          const form = new FormData();
          Object.entries(changedFields).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              form.append(key, value);
            }
          });

          if (module.id) {
            await editModule(activeCourseId, module.id, form);
          } else if (!module.isDeleted) {
            await submitModule(activeCourseId, form);
          }
        } catch (err) {
          setIsSubmitting(false);
          return;
        }
      }

      toast.success(
        isEditMode
          ? "Course updated successfully"
          : "Course created successfully",
      );
      navigate(TRAINING);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading && isEditMode) {
    return (
      <div className="flex items-center justify-center h-[70vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  /* ----------------------------------
     UI
  ---------------------------------- */
  return (
    <div className="flex items-center justify-center pb-3">
      <div className=" bg-white  w-full lg:mx-auto max-w-full rounded-2xl p-4 relative">
        {/* Header */}
        <div className="flex justify-between max-sm:mt-16 mb-4">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)}>
              <IoMdArrowRoundBack size={23} className="text-primary" />
            </button>

            <h2 className="text-xl font-semibold text-primary">
              {isEditMode ? "Edit Course" : "Add Course"}
            </h2>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* LEFT */}
          <div className="flex flex-col gap-1">
            <h3 className="font-semibold text-primary mb-6">Course Details</h3>
            <label className="text-sm font-medium">Course Title</label>
            <input
              className={inputClasses}
              value={course.title}
              onChange={(e) => setCourse({ ...course, title: e.target.value })}
            />

            <label className="text-sm font-medium mb10">Description</label>
            <textarea
              name="description"
              placeholder="Enter the description"
              value={course.description}
              onChange={(e) =>
                setCourse({ ...course, description: e.target.value })
              }
              rows={3}
              className={inputClasses}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
              {/* Course Order */}
              <div>
                <label className="text-sm font-medium text-black">
                  Course Order
                </label>
                <input
                  type="number"
                  min="1"
                  className={inputClasses}
                  value={course.order}
                  onChange={(e) =>
                    setCourse({
                      ...course,
                      order:
                        e.target.value === "" ? "" : Number(e.target.value),
                    })
                  }
                />
              </div>

              {/* Course Status */}
              <div className="flex flex-col  items-end">
                <label className="text-sm font-medium mb-1">
                  Course Status
                </label>

                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={course.is_active}
                    onChange={(e) =>
                      setCourse({ ...course, is_active: e.target.checked })
                    }
                  />
                  Active
                </label>
              </div>
            </div>

            <label className="text-sm font-medium block mb-1">
              Course Thumbnail
            </label>

            <div className="flex items-center justify-between max-w-full flex-wrap gap-3 text-sm">
              {/* hidden input */}
              <input
                type="file"
                id="course-file"
                className="border-1"
                onChange={(e) =>
                  setCourse({
                    ...course,
                    image: e.target.files[0],
                  })
                }
              />

              {/* filename / view logic */}
              {course.existingImage ? (
                <div className="flex items-center gap-2">
                  <span className="truncate max-w-[220px]">
                    {course.existingImage.split("/").pop()}
                  </span>
                  <a
                    href={course.existingImage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    View
                  </a>
                </div>
              ) : (
                ""
              )}
            </div>
          </div>

          {/* RIGHT */}
          <div>
            <div className="text-sm font-medium flex justify-between text-primary mb-2">
              <h3>Modules</h3>
              <button
                onClick={handleAddModule}
                className="flex items-center gap-2 px-3 py-2 bg-primary text-white rounded-md
             hover:bg-primary/90 transition text-sm"
              >
                <FiPlus />
                Add Module
              </button>
            </div>
            <div className="h-[64vh] overflow-y-auto">
              {modules.map((module, index) => {
                return (
                  !module.isDeleted && (
                    <div key={index} className="p-2 mb-2 rounded">
                      {/* Header row: title + delete */}
                      <div className="flex items-center justify-between mb-1">
                        <label className="text-sm font-medium">
                          Module Title
                        </label>

                        <button
                          onClick={() => handleRemoveModule(index)}
                          className="text-red-600 hover:text-red-700"
                          title="Delete module"
                        >
                          <FiTrash2 />
                        </button>
                      </div>

                      <input
                        className={inputClasses}
                        placeholder="Module Title"
                        value={module.title}
                        onChange={(e) =>
                          handleModuleChange(index, "title", e.target.value)
                        }
                      />

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2">
                        {/* Module Order */}
                        <div>
                          <label className="text-sm font-medium">
                            Module Order
                          </label>
                          <input
                            type="number"
                            min="1"
                            className={inputClasses}
                            value={module.order}
                            onChange={(e) =>
                              handleModuleChange(index, "order", e.target.value)
                            }
                          />
                        </div>

                        {/* Content Type */}
                        <div>
                          <label className="text-sm font-medium">
                            Content Type
                          </label>
                          <select
                            className={inputClasses}
                            value={module.content_type}
                            onChange={(e) =>
                              handleModuleChange(
                                index,
                                "content_type",
                                e.target.value,
                              )
                            }
                          >
                            {MODULE_TYPES.map((t) => (
                              <option key={t.value} value={t.value}>
                                {t.label}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Duration */}
                        <div>
                          <label className="text-sm font-medium">
                            Duration (min)
                          </label>
                          <input
                            type="number"
                            min="1"
                            placeholder="e.g. 10"
                            className={inputClasses}
                            value={module.duration_minutes}
                            onChange={(e) =>
                              handleModuleChange(
                                index,
                                "duration_minutes",
                                e.target.value,
                              )
                            }
                          />
                        </div>
                      </div>

                      <div className="flex justify-between items-center flex-wrap">
                        <input
                          type="file"
                          accept={
                            module.content_type === "video"
                              ? "video/*"
                              : module.content_type === "pdf"
                                ? "application/pdf"
                                : ".ppt,.pptx"
                          }
                          onChange={(e) =>
                            handleModuleChange(index, "file", e.target.files[0])
                          }
                        />

                        {module.prev_file && (
                          <div className="flex items-center gap-2 max-w-[250px] md:max-w-[300px]">
                            <span className="truncate">
                              {getFileName(module.prev_file)}
                            </span>

                            {typeof module.prev_file === "string" && (
                              <a
                                href={module.prev_file}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary underline"
                              >
                                View
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={`px-4 py-2 rounded text-white flex items-center gap-2
            ${isSubmitting ? "bg-primary/70 cursor-not-allowed" : "bg-primary"}`}
          >
            {isSubmitting && (
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
            )}
            {isEditMode ? "Save Changes" : "Create Course"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CourseActionPage;
